/**
 * esportaCsv — gli iscritti di un questionario in un file per Excel. PURO (tranne `scaricaCsv`).
 *
 * Formato per l'Excel italiano: UTF-8 con BOM (gli accenti restano accenti), separatore punto e
 * virgola, una colonna per domanda col suo nome breve e il valore leggibile (etichette, non codici).
 *
 * ⚠️ I testi arrivano da un modulo PUBBLICO: un valore che comincia con «=», «+», «-», «@» verrebbe
 * eseguito da Excel come formula (iniezione nei CSV, OWASP). Si neutralizza con un apostrofo davanti.
 */

import { valoreLeggibile, type Domanda } from '@shared/questionari';
import type { PlatformLead, PlatformQuestionnaireResponse } from '../../../types/platformCrm';
import { CONSENSO_LABELS, formatDateTime, nomeLingua } from '../labels';
import { contattiDi, provenienzaDi, statoConsenso } from './filtri';

const SEPARATORE = ';';

/** Una cella pronta: formula neutralizzata, virgolette se serve. */
export function cella(valore: string | number | null | undefined): string {
  let testo = valore === null || valore === undefined ? '' : String(valore);
  if (/^[=+\-@\t\r]/.test(testo)) testo = `'${testo}`;
  if (/[";\n\r]/.test(testo)) testo = `"${testo.replace(/"/g, '""')}"`;
  return testo;
}

export function csvIscritti(
  domande: Domanda[],
  risposte: PlatformQuestionnaireResponse[],
  lead: Map<string, PlatformLead>,
): string {
  const intestazione = [
    'Data', 'Nome', 'Ruolo', 'Azienda', 'Email', 'Telefono', 'Partita IVA', 'Città', 'Provincia',
    'Provenienza', 'utm_source', 'utm_medium', 'utm_campaign', 'Lingua', 'Consenso aggiornamenti', 'Non contattare',
    ...domande.map((d) => d.nome_breve || d.testo),
  ];
  const righe = risposte.map((r) => {
    const l = lead.get(r.platform_lead_id);
    const c = contattiDi(r, l);
    const perDomanda = domande.map((d) => {
      const riga = (r.risposte ?? []).find((x) => x.domanda_id === d.id);
      return riga ? valoreLeggibile(d, riga.valore, riga.dettaglio) : '';
    });
    return [
      formatDateTime(r.completato_at),
      c.nome, c.ruolo, c.azienda, c.email, c.telefono, c.partita_iva, c.citta, c.provincia,
      provenienzaDi(r),
      r.provenienza?.utm_source ?? '', r.provenienza?.utm_medium ?? '', r.provenienza?.utm_campaign ?? '',
      r.lingua ? nomeLingua(r.lingua) : '',
      CONSENSO_LABELS[statoConsenso(l)],
      l?.non_contattare ? 'sì' : '',
      ...perDomanda,
    ];
  });
  return [intestazione, ...righe].map((riga) => riga.map(cella).join(SEPARATORE)).join('\r\n');
}

/** Il nome del file: titolo semplificato e data di oggi. */
export function nomeFileCsv(titolo: string, oggi = new Date()): string {
  const base = titolo
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 50) || 'questionario';
  return `iscritti-${base}-${oggi.toISOString().slice(0, 10)}.csv`;
}

/** Fa scaricare il file al browser (BOM in testa per Excel). */
export function scaricaCsv(contenuto: string, nomeFile: string): void {
  const blob = new Blob(['﻿', contenuto], { type: 'text/csv;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = nomeFile;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}
